import {
  DeviceIsDeviceType,
  DeviceIsSystemType,
} from 'features/Integrations/utils/deviceComparison.utils';
import { DEVICE_DATA_MAX_IDS_PER_REQUEST } from 'services/devices/devices.api';
import {
  buildStageDeviceMap,
  buildColorMap,
  buildTimeStampSet,
  getYAxisValue,
  isSelectedAndHasDataWithinTimeRange,
  recordAlertEventAndGetLevel,
} from './deviceGraphHelpers.utils';

export const getStageDevices = (devices) => {
  return devices.filter(
    (device) => DeviceIsSystemType(device, 'Flood') && DeviceIsDeviceType(device, 'Stage'),
  );
};

export const WIDGET_TIME_OPTIONS = [
  { label: '1 Minute', value: 1 },
  { label: '2 Minutes', value: 2 },
  { label: '5 Minutes', value: 5 },
  { label: '10 Minutes', value: 10 },
  { label: '15 Minutes', value: 15 },
  { label: '30 Minutes', value: 30 },
  { label: '1 Hour', value: 60 },
  { label: '2 Hours', value: 120 },
  { label: '3 Hours', value: 180 },
  { label: '4 Hours', value: 240 },
  { label: '5 Hours', value: 300 },
  { label: '10 Hours', value: 600 },
  { label: '12 Hours', value: 720 },
  { label: '1 Day', value: 1440 },
  { label: '7 Days', value: 10080 },
  { label: '30 Days', value: 43200 },
];

export const DEFAULT_WIDGET_TIME_INDEX = WIDGET_TIME_OPTIONS.findIndex(
  (option) => option.value === 1440,
);

export const getWidgetTimeRangeFromOption = (selectedIndex = DEFAULT_WIDGET_TIME_INDEX) => {
  const minutes = WIDGET_TIME_OPTIONS[selectedIndex]?.value;
  if (!minutes) return null;

  const endTime = Date.now();
  return {
    startTime: endTime - minutes * 60 * 1000,
    endTime,
  };
};

/**
 * Points for each device in ONE graph request. The backend's maximum
 * (`max-limit`, CONTRACT.md tier 2). The chart draws a whole window, so it asks
 * for as much of that window per round trip as the API allows.
 */
export const GRAPH_SERIES_PAGE_LIMIT = 5000;

/**
 * Pages the chart will walk for ONE device in ONE range before it stops.
 *
 * BE-03's MEASUREMENTS.md puts the busiest DEV device at ~27,300 rows over 30
 * days — the widest range the controls offer — so six 5,000-point pages cover
 * the measured worst case with headroom. The ceiling exists for the case the
 * measurement does not cover: a device writing far faster than a gauge would
 * otherwise page without end, and the browser would hold (and ECharts draw)
 * a series no one can read. When it is hit, the chart SAYS the series is
 * bounded instead of drawing a short line across a full-width axis.
 */
export const MAX_GRAPH_PAGES_PER_DEVICE = 6;

/** The ceiling above, in points — what the bounded-series notice reports. */
export const MAX_GRAPH_POINTS_PER_DEVICE = GRAPH_SERIES_PAGE_LIMIT * MAX_GRAPH_PAGES_PER_DEVICE;

/**
 * Fetches the WHOLE window for `ids`, one page at a time.
 *
 * The tier-2 endpoint answers at most `limit` points for each device and sets
 * `page.hasMore` when a device filled its page, so a single request is the
 * NEWEST slice of the window, not the window. This walks `offset` forward by
 * `limit` until `hasMore` is false (or the ceiling stops it) and sends the ids
 * in chunks the endpoint accepts (it answers 400 above
 * `DEVICE_DATA_MAX_IDS_PER_REQUEST`).
 *
 * Nothing is returned but the two facts the caller renders: each page lands in
 * the `getConnectedDevices` cache through the lazy query's `onQueryStarted`
 * merge, which unions the points by timestamp, so later pages ADD older points
 * to the series rather than replacing it.
 *
 * `isCurrent` is checked after every answer: a caller whose window has moved on
 * stops paging for the old one and reports `superseded` so it can leave the new
 * window's state alone.
 *
 * @param {{
 *   fetchRange: (args: object, preferCacheValue?: boolean) => { unwrap: () => Promise<object> },
 *   ids: Array<number>,
 *   startTime: number,
 *   endTime: number,
 *   limit?: number,
 *   maxPages?: number,
 *   isCurrent?: () => boolean,
 * }} options
 * @returns {Promise<{ capped: boolean, superseded: boolean }>}
 */
export const fetchGraphSeriesPages = async ({
  fetchRange,
  ids,
  startTime,
  endTime,
  limit = GRAPH_SERIES_PAGE_LIMIT,
  maxPages = MAX_GRAPH_PAGES_PER_DEVICE,
  isCurrent,
}) => {
  const idList = Array.isArray(ids) ? ids : [];
  let capped = false;

  for (let index = 0; index < idList.length; index += DEVICE_DATA_MAX_IDS_PER_REQUEST) {
    const chunk = idList.slice(index, index + DEVICE_DATA_MAX_IDS_PER_REQUEST);
    let offset = 0;
    let pages = 0;
    let hasMore = true;

    while (hasMore && pages < maxPages) {
      const response = await fetchRange(
        { startTime, endTime, ids: chunk, limit, offset },
        true,
      ).unwrap();
      if (isCurrent && !isCurrent()) return { capped, superseded: true };
      pages += 1;
      hasMore = Boolean(response?.page?.hasMore);
      offset += limit;
    }

    // Still more to come after the last page allowed: this chunk is bounded.
    if (hasMore) capped = true;
  }

  return { capped, superseded: false };
};

// Gets the y-axis label (name) based on selected data type and unit type
export const getYAxisLabel = (selectedDataType, unitType) => {
  switch (selectedDataType) {
    case 'sensordepths':
    case 'depths':
      return unitType ? 'Depth (m)' : 'Depth (ft)';
    case 'temp':
      return unitType ? 'Temperature (C)' : 'Temperature (F)';
    case 'baro':
      return unitType ? 'Pressure (mBar)' : 'Pressure (inHg)';
    case 'battery':
      return 'Battery (V)';
    case 'cell signal':
      return 'Cell Signal (dBm)';
    case 'intensity':
      return 'Rain Intensity';
    case 'rainfall':
      return 'Rainfall';
    case 'tips':
      return 'Bucket Tips';
    default:
      return null;
  }
};

// Determines the series type (line chart or bar chart) based on selected data type
export const getSeriesType = (selectedDataType) => {
  if (
    selectedDataType === 'rainfall' ||
    selectedDataType === 'tips' ||
    selectedDataType === 'intensity'
  ) {
    return 'bar';
  }
  return 'line';
};

/**
 * Build logic model for AWARE device chart:
 * - labels: x-axis labels (timestamps)
 * - datasets: logical datasets with meta + color (not tied to any chart lib)
 * - markLineData: [{ xAxis: "timestamp" }, ...] for red-line thresholds
 *
 * @returns {{ labels: string[], datasets: any[], markLineData: {xAxis:string}[] }}
 */

const emptyChart = { labels: [], datasets: [], markLineData: [] };

const buildDatasets = (
  stageDeviceMap,
  selectedDeviceMap,
  filterWithinTimeRange,
  floodAlertEvents,
  selectedDataType,
  colors,
  unitType,
) => {
  return Object.keys(selectedDeviceMap)
    .filter((deviceId) => {
      return isSelectedAndHasDataWithinTimeRange(
        deviceId,
        selectedDeviceMap,
        stageDeviceMap,
        filterWithinTimeRange,
      ); // Skips unselected devices, devices with no data, and devices with no data in time range
    })
    .map((deviceId) => {
      // Creates an echarts series for each device
      const device = stageDeviceMap.get(String(deviceId)); // Gets the aware device that matches the currently selected id
      if (!device) return null;

      const filteredData = device.data
        .filter(filterWithinTimeRange)
        .map((deviceData) => {
          const actualMeasuredWaterDepth = deviceData['sensordepths'];
          const alertLevel = recordAlertEventAndGetLevel(
            actualMeasuredWaterDepth,
            device.device_settings,
            floodAlertEvents,
            deviceData,
          );

          return {
            ...deviceData,
            floodAlertLevel: alertLevel,
          };
        })
        .sort((a, b) => a.ts - b.ts);

      const datapoints = filteredData.map((deviceData) => {
        const { ts: timestamp } = deviceData;
        const yValue = getYAxisValue(deviceData, selectedDataType, unitType);
        return [timestamp, yValue]; // ECharts format: [x, y]
      });
      return {
        _meta: { ...device, data: filteredData }, // Stores the device object from the stageDeviceMap and its filtered data
        label: `${device.static_id} ${device.alias}`, // For the name used in legend and tooltips
        data: datapoints, // ECharts points to plot as [x, y]
        color: colors[device.id],
      };
    })
    .filter(Boolean)
    .reverse();
};

export const buildStageDeviceChartModel = ({
  devices = [],
  selectedDeviceMap = {}, // { [alias]: boolean }
  selectedDataType = 'sensordepths',
  unitType = false,
  startTime,
  endTime,
}) => {
  if (!devices || devices.length === 0 || !startTime || !endTime) {
    return emptyChart;
  }

  const stageDevices = getStageDevices(devices);
  const stageDeviceMap = buildStageDeviceMap(stageDevices);

  if (stageDevices.length === 0) {
    return emptyChart;
  }

  const colors = buildColorMap(stageDevices); // Contains all colors for devices
  const timestampSet = buildTimeStampSet(startTime, endTime); // Contains all timestamps for x-axis labels
  const floodAlertEvents = new Map(); // Stores timestamps where any flood device crossed a threshold (keyed by ts for O(1) lookups)

  // Checks to ensure data point is within the start and end time
  const filterWithinTimeRange = (dataPoint) => {
    const date = new Date(dataPoint.ts); // Converts data point timestamp into a date object
    return date >= startTime && date <= endTime; // Check if data point is within the start and end time
  };

  const datasets = buildDatasets(
    stageDeviceMap,
    selectedDeviceMap,
    filterWithinTimeRange,
    floodAlertEvents,
    selectedDataType,
    colors,
    unitType,
  );

  //TODO: Figure out how to display string labels, but use numeric x-axis values
  const labels = [...timestampSet].map((ts) => new Date(ts).toLocaleDateString());

  if (!datasets.length) {
    return emptyChart;
  }

  // Convert threshold events into ECharts-ready markLineData with colors
  const alertLevelColors = {
    1: '#ffCC00', // Yellow for alert level 1
    2: '#ff7b00ff', // Orange for alert level 2
    3: '#ff0000', // Red for alert level 3
  };

  const markLineData = [...floodAlertEvents.values()].map((event) => ({
    xAxis: event.timestamp,
    level: event.level,
    color: alertLevelColors[event.level],
  }));

  // labels = x-axis labels (timestamps)
  // datasets = points to plot with meta info (colors and etc)
  // markLineData = flood alert level lines (vertical yellow, orange, and red lines on chart)
  return { labels, datasets, markLineData };
};

// Creates a custom tooltip that appears when hovering over data points in the AWARE device chart
export const buildStageTooltipFormatter = (datasets, imageMap = new Map(), unitType = false) => {
  return (params) => {
    const items = Array.isArray(params) ? params : [params];
    if (!items.length) return null;

    const ts = items[0].axisValue ?? (Array.isArray(items[0].value) ? items[0].value[0] : null);

    const label = ts ? new Date(ts).toLocaleString() : '';

    const lines = [`<strong>${label}</strong>`];
    let imageForTooltip = null;

    items.forEach((pointParam) => {
      const dataset = datasets[pointParam.seriesIndex];
      const raw = dataset?._meta?.byTimestampMap?.get(ts);

      if (raw) {
        const actualDepth = Number(raw.sensordepths ?? raw.depth1 ?? 0);
        const sensorDepth = Number(raw.depth1 ?? 0);
        const temperature = Number(raw.h2oTemp ?? 0);
        const pressure = Number(raw.baro ?? 0);
        const battery = Number(raw.battery ?? 0);
        const floodAlertLevel = Number(raw.floodAlertLevel ?? 0);

        const alert =
          floodAlertLevel >= 3
            ? 'Threshold 3'
            : floodAlertLevel === 2
              ? 'Threshold 2'
              : floodAlertLevel === 1
                ? 'Threshold 1'
                : 'None';

        const actualDepthFeet = actualDepth / 12;
        const sensorDepthFeet = sensorDepth / 12;
        const actualDepthMeters = actualDepth * 0.0254;
        const sensorDepthMeters = sensorDepth * 0.0254;

        const depthLines = unitType
          ? [
              `Actual Depth (m): ${actualDepthMeters.toFixed(2)} m`,
              `Sensor Depth (m): ${sensorDepthMeters.toFixed(2)} m`,
            ]
          : [
              `Actual Depth (in/ft): ${actualDepth.toFixed(
                2,
              )} in / ${actualDepthFeet.toFixed(2)} ft`,
              `Sensor Depth (in/ft): ${sensorDepth.toFixed(
                2,
              )} in / ${sensorDepthFeet.toFixed(2)} ft`,
            ];

        lines.push(
          `<span>${pointParam.marker}${pointParam.seriesName}</span>`,
          ...depthLines,
          `Temperature (F/C): ${((temperature * 9) / 5 + 32).toFixed(
            2,
          )} F / ${temperature.toFixed(2)} C`,
          `Pressure (mBar/inHg): ${pressure.toFixed(2)} mBar / ${(pressure / 33.864).toFixed(
            2,
          )} inHg`,
          `Battery (V): ${battery}V`,
          `Alert: ${alert}`,
        );
        if (raw.did == null && raw.ts == null) {
          lines.push('<span style="opacity:.75;">No datasheet for this point</span>');
        }

        if (!imageForTooltip && raw?.img_link_url) {
          imageForTooltip =
            (imageMap instanceof Map
              ? imageMap.get(raw.img_link_url)
              : imageMap?.[raw.img_link_url]) || raw.img_link_url;
        }
      } else {
        const y = Array.isArray(pointParam.value) ? pointParam.value[1] : pointParam.value;
        lines.push(`${pointParam.marker}${pointParam.seriesName}: ${y}`);
      }
    });

    if (imageForTooltip) {
      lines.push(
        `<div style="margin-top:8px;"><img src="${imageForTooltip}" alt="Aware Camera Capture" style="max-width:240px; max-height:160px; display:block;" /></div>`,
      );
    }

    return `<div class="echart-tooltip-content">${lines.join('<br/>')}</div>`;
  };
};

/**
 * Given a group ID, the full groups list, and the set of graph-capable device IDs,
 * returns a selectedDeviceMap for the graph containing only the
 * applicable devices in the group, or null if none qualify.
 *
 * @param {number|null} linkingGroupId
 * @param {object[]} allGroups
 * @param {string[]} graphDeviceIds - string IDs of all graph-capable devices
 * @returns {Record<string, true> | null}
 */
export const resolveGroupDeviceMap = (linkingGroupId, allGroups, graphDeviceIds) => {
  if (linkingGroupId == null) return null;
  const group = allGroups.find((g) => g.id === linkingGroupId);
  if (!group) return null;
  const groupDeviceIds = (group.devices || []).map(String);
  const applicableIds = groupDeviceIds.filter((id) => graphDeviceIds.includes(id));
  if (applicableIds.length === 0) return null;
  const map = {};
  applicableIds.forEach((id) => {
    map[id] = true;
  });
  return map;
};
